import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CheckCircle2, Pencil, PowerOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { api, UGC_RETURN_WINDOW_DAYS, UGC_TEXTS, type UgcAdminArticleOrder, type UgcArticleEvent } from "@/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyButton, dateRu, dateTimeRu, num, rub, StatTile } from "@/components/promo-kit";
import { UGC_ACTION_LABEL, UGC_CHANNEL_LABEL, UGC_ROLE_LABEL, UgcArticleDialog, UgcCommissionBadge, UgcOwnerBadge, UgcProductLink, type UgcStatusAction, UgcStatusBadge, UgcStatusDialog, ugcActionsFor, ugcOwnerText } from "@/components/ugc-kit";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Button } from "@osmix/ui/button";
import { useCanManagePromos } from "./promos";
const ACTION_ICON: Record<UgcStatusAction, typeof Ban> = {
  active: CheckCircle2,
  disabled: PowerOff,
  blocked: Ban
};
const EVENT_LABEL: Record<UgcArticleEvent["kind"], string> = {
  created: "Создан",
  status: "Статус",
  visit: "Переход",
  order: "Заказ",
  commission: "Комиссия",
  edit: "Правка"
};
const EVENT_DOT: Record<UgcArticleEvent["kind"], string> = {
  created: "bg-primary",
  status: "bg-red-500",
  visit: "bg-sky-500",
  order: "bg-emerald-500",
  commission: "bg-amber-500",
  edit: "bg-muted-foreground"
};

/**
 * Карточка цифрового артикула в консоли: поля, метрики, связанные заказы с
 * состоянием комиссии, журнал событий. Действия - принудительно одобрить,
 * отключить, заблокировать (с причиной), правка названия и площадки, отметка
 * «Выплачено» по комиссии, ставшей доступной к выплате.
 */
export function UgcArticleDetailPage() {
  const {
    t
  } = useTranslation();
  const {
    id = ""
  } = useParams<{
    id: string;
  }>();
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  const q = useQuery({
    queryKey: ["ugc", "detail", id],
    queryFn: () => api.ugc.get(id)
  });
  const [action, setAction] = useState<UgcStatusAction | null>(null);
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState<UgcAdminArticleOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({
    queryKey: ["ugc"]
  });
  const back = <Link to="/promos?tab=ugc" className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
			<ArrowLeft className="size-4" />
			UGC-Артикулы
		</Link>;
  if (q.error) return <div className="flex flex-col gap-4">
				{back}
				<p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{(q.error as Error).message}</p>
			</div>;
  if (!q.data) return <div className="flex flex-col gap-4">
				{back}
				<p className="text-sm text-muted-foreground">Загружаем…</p>
			</div>;
  const a = q.data;
  const m = a.metrics;
  const noStats = m.clicks === 0 && m.orders === 0;
  const partner = a.ownerKind === "partner";
  const markPaid = async () => {
    if (!paying?.commissionId) return;
    setBusy(true);
    setError(null);
    try {
      await api.ugc.markPaid(paying.commissionId);
      setPaying(null);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
      setPaying(null);
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex min-w-0 flex-col gap-6" data-testid="ugc-detail">
			{back}

			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="font-mono text-2xl font-semibold tracking-wider tabular-nums" data-testid="ugc-detail-code">
							{a.code}
						</h1>
						<CopyButton value={a.code} label="Скопировать" testId="ugc-detail-copy" />
						<UgcStatusBadge status={a.status} />
						<UgcOwnerBadge owner={a.ownerKind} />
					</div>
					<p className="mt-1 break-words text-sm">{a.name}</p>
					{a.statusReason && <p className={cn("mt-2 max-w-2xl rounded-md px-3 py-2 text-sm", a.status === "blocked" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground")} data-testid="ugc-detail-reason">
							Причина: {a.statusReason}
						</p>}
				</div>
				{canManage && <div className="flex flex-wrap gap-2" data-testid="ugc-actions" data-tour="ugc-card-actions">
						{ugcActionsFor(a.status).map(act => {
          const Icon = ACTION_ICON[act];
          return <Button key={act} size="sm" variant={act === "blocked" ? "destructive" : act === "active" ? "default" : "outline"} onClick={() => setAction(act)} data-testid={`ugc-action-${act}`}>
									<Icon className="mr-1.5 size-4" />
									{UGC_ACTION_LABEL(act, a.status)}
								</Button>;
        })}
						{a.status !== "deleted" && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="ugc-action-edit">
								<Pencil className="mr-1.5 size-4" />
								Изменить
							</Button>}
					</div>}
			</div>

			{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

			<dl className="grid gap-x-6 gap-y-3 rounded-lg border p-4 text-sm sm:grid-cols-2 xl:grid-cols-3" data-testid="ugc-detail-fields">
				<Field label="Товар">
					<UgcProductLink id={a.productId} title={a.productTitle} />
				</Field>
				<Field label="Магазин">{a.shopName}</Field>
				<Field label="Владелец">
					{ugcOwnerText(a)}
					{a.partnerRole && <span className="text-muted-foreground"> · {UGC_ROLE_LABEL[a.partnerRole]}</span>}
				</Field>
				<Field label="Площадка / канал">
					{UGC_CHANNEL_LABEL[a.channel]}
					{a.placement && <span className="text-muted-foreground"> · {a.placement}</span>}
				</Field>
				<Field label="Кампания">{a.campaign || <span className="text-muted-foreground">—</span>}</Field>
				<Field label="Комиссия партнёра">
					{partner ? `${num(a.commissionPercent)} % от выкупленных позиций магазина после скидок` : <span className="text-muted-foreground">не начисляется</span>}
				</Field>
				<Field label="Создан">{dateTimeRu(a.createdAt)}</Field>
				<Field label="Изменён">{dateTimeRu(a.updatedAt)}</Field>
			</dl>

			<section className="flex flex-col gap-2">
				<h2 className="text-sm font-medium">Метрики за всё время</h2>
				{noStats ? <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground" data-testid="ugc-detail-nostats">
						{UGC_TEXTS.noStats}
					</p> : <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="ugc-detail-metrics">
						<StatTile label="Переходы" value={num(m.clicks)} hint={`CR ${num(m.cr)} %`} />
						<StatTile label="Заказы" value={num(m.orders)} hint={`оплачено ${num(m.paidOrders)}`} />
						<StatTile label="Выкуплено" value={num(m.completedOrders)} hint={`возвраты и отмены: ${num(m.returns)}`} />
						<StatTile label="GMV" value={rub(m.gmv)} hint={partner ? `комиссия ${rub(m.commission)}` : undefined} />
					</div>}
			</section>

			<section className="flex flex-col gap-2" data-testid="ugc-detail-orders" data-tour="ugc-card-orders">
				<h2 className="text-sm font-medium">
					Связанные заказы <span className="text-muted-foreground">{num(a.orders.length)}</span>
				</h2>
				{partner && <p className="text-xs text-muted-foreground">
						Комиссия в HOLD, пока заказ не завершён; через {UGC_RETURN_WINDOW_DAYS} дней после завершения - «Доступно к выплате». Отмена и возврат аннулируют начисление.
					</p>}
				{/* Телефон: строки заказов карточками. */}
				<div className="flex flex-col gap-2 md:hidden">
					{a.orders.length === 0 && <p className="rounded-lg border py-6 text-center text-sm text-muted-foreground">Заказов по артикулу пока нет</p>}
					{a.orders.map(o => <div key={o.orderId} className="flex flex-col gap-2 rounded-lg border p-3 text-sm" data-testid={`ugc-order-card-${o.orderId}`}>
							<div className="flex items-center justify-between gap-2">
								<Link className="font-medium hover:underline" to={`/orders?order=${encodeURIComponent(o.orderId)}`}>
									№ {o.orderNumber ?? o.orderId}
								</Link>
								<span className="text-xs text-muted-foreground">{dateRu(o.createdAt)}</span>
							</div>
							<div className="flex flex-wrap items-center justify-between gap-2">
								<span className="text-muted-foreground">{t(`orders.status.${o.status}`, {
                defaultValue: o.status
              })}</span>
								<span className="tabular-nums">{rub(o.base)}</span>
							</div>
							{o.commissionState && <div className="flex flex-wrap items-center justify-between gap-2">
									<UgcCommissionBadge state={o.commissionState} />
									<span className="tabular-nums">{rub(o.commission)}</span>
								</div>}
							{canManage && o.commissionState === "available" && <Button size="sm" variant="outline" onClick={() => setPaying(o)} data-testid={`ugc-pay-${o.orderId}`}>
									Отметить «Выплачено»
								</Button>}
						</div>)}
				</div>
				<div className="hidden overflow-x-auto rounded-lg border md:block">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Заказ</TableHead>
								<TableHead>Дата</TableHead>
								<TableHead>Статус заказа</TableHead>
								<TableHead className="text-right">База</TableHead>
								<TableHead className="text-right">Комиссия</TableHead>
								<TableHead>Состояние комиссии</TableHead>
								<TableHead className="text-right" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{a.orders.length === 0 && <TableRow>
									<TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
										Заказов по артикулу пока нет
									</TableCell>
								</TableRow>}
							{a.orders.map(o_0 => <TableRow key={o_0.orderId} data-testid={`ugc-order-${o_0.orderId}`}>
									<TableCell className="font-medium">
										<Link className="hover:underline" to={`/orders?order=${encodeURIComponent(o_0.orderId)}`}>
											№ {o_0.orderNumber ?? o_0.orderId}
										</Link>
									</TableCell>
									<TableCell className="whitespace-nowrap text-sm text-muted-foreground">{dateTimeRu(o_0.createdAt)}</TableCell>
									<TableCell className="text-sm">{t(`orders.status.${o_0.status}`, {
                  defaultValue: o_0.status
                })}</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">{rub(o_0.base)}</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">{o_0.commissionState ? rub(o_0.commission) : "—"}</TableCell>
									<TableCell>{o_0.commissionState ? <UgcCommissionBadge state={o_0.commissionState} /> : <span className="text-sm text-muted-foreground">—</span>}</TableCell>
									<TableCell className="text-right">
										{canManage && o_0.commissionState === "available" && <Button size="sm" variant="outline" onClick={() => setPaying(o_0)} data-testid={`ugc-pay-${o_0.orderId}`}>
												Выплачено
											</Button>}
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>
				</div>
			</section>

			<section className="flex flex-col gap-2" data-testid="ugc-detail-events" data-tour="ugc-card-events">
				<h2 className="text-sm font-medium">Журнал событий</h2>
				{a.events.length === 0 ? <p className="text-sm text-muted-foreground">Событий пока нет</p> : <ol className="flex flex-col rounded-lg border">
						{a.events.map((ev, i) => <li key={`${ev.at}-${i}`} className="flex min-w-0 gap-3 border-b px-4 py-3 text-sm last:border-b-0">
								<span className={cn("mt-1.5 size-2 shrink-0 rounded-full", EVENT_DOT[ev.kind])} />
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
										<span className="font-medium text-foreground">{EVENT_LABEL[ev.kind]}</span>
										<span>{dateTimeRu(ev.at)}</span>
										{ev.actor && <span className="truncate">· {ev.actor}</span>}
									</div>
									<p className="mt-0.5 break-words">{ev.text}</p>
								</div>
							</li>)}
					</ol>}
			</section>

			{action && <UgcStatusDialog article={a} action={action} onClose={() => setAction(null)} onDone={async () => {
      setAction(null);
      await refresh();
    }} />}
			{editing && <UgcArticleDialog open editing={a} onClose={() => setEditing(false)} onSaved={async () => {
      setEditing(false);
      await refresh();
    }} />}
			<ConfirmDialog open={paying !== null} title="Комиссия выплачена партнёру?" description={paying ? `Заказ № ${paying.orderNumber ?? paying.orderId}: ${rub(paying.commission)} партнёру ${a.partnerName ?? ""}. Отметка переводит начисление в «Выплачено» и попадает в журнал.` : undefined} confirmLabel={busy ? "Сохраняем…" : "Выплачено"} busy={busy} onConfirm={() => void markPaid()} onClose={() => setPaying(null)} />
		</div>;
}
function Field(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <dt className="text-xs text-muted-foreground">{label}</dt>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <dd className="mt-0.5 break-words">{children}</dd>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}